/**
 * Copyright 2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { OptimizelyError } from "../../../error/optimizly_error";
import { CMAB_FETCH_FAILED, INVALID_CMAB_FETCH_RESPONSE } from "../../../message/error_message";
import { runWithRetry } from "../../../utils/executor/backoff_retry_runner";
import { sprintf } from "../../../utils/fns";
import { isSuccessStatusCode } from "../../../utils/http_request_handler/http_util";
const DEFAULT_CMAB_PREDICTION_ENDPOINT = 'https://prediction.cmab.optimizely.com/predict/%s';
export class DefaultCmabClient {
    constructor(config) {
        this.predictionEndpointTemplate = DEFAULT_CMAB_PREDICTION_ENDPOINT;
        this.requestHandler = config.requestHandler;
        this.retryConfig = config.retryConfig;
        if (config.predictionEndpointTemplate) {
            this.predictionEndpointTemplate = config.predictionEndpointTemplate;
        }
    }
    fetchDecision(ruleId, userId, attributes, cmabUuid) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const url = sprintf(this.predictionEndpointTemplate, ruleId);
            const cmabAttributes = Object.keys(attributes).map((key) => ({
                id: key,
                value: attributes[key],
                type: 'custom_attribute',
            }));
            const body = {
                instances: [
                    {
                        visitorId: userId,
                        experimentId: ruleId,
                        attributes: cmabAttributes,
                        cmabUUID: cmabUuid,
                    }
                ]
            };
            const variation = yield (this.retryConfig ?
                runWithRetry(() => this.doFetch(url, JSON.stringify(body)), (_b = (_a = this.retryConfig).backoffProvider) === null || _b === void 0 ? void 0 : _b.call(_a), this.retryConfig.maxRetries).result : this.doFetch(url, JSON.stringify(body)));
            return variation;
        });
    }
    doFetch(url, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.requestHandler.makeRequest(url, { 'Content-Type': 'application/json' }, 'POST', data).responsePromise;
            if (!isSuccessStatusCode(response.statusCode)) {
                return Promise.reject(new OptimizelyError(CMAB_FETCH_FAILED, response.statusCode));
            }
            const body = JSON.parse(response.body);
            if (!this.validateResponse(body)) {
                return Promise.reject(new OptimizelyError(INVALID_CMAB_FETCH_RESPONSE));
            }
            return String(body.predictions[0].variation_id);
        });
    }
    validateResponse(body) {
        return body.predictions && body.predictions.length > 0 && body.predictions[0].variation_id;
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=cmab_client.js.map